import { InputHTMLAttributes, ReactNode, useId, useState } from 'react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'onChange'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Trailing label — can carry inline links/buttons (e.g. "I agree to the Terms of Service"). */
  label?: ReactNode;
  /** 'default' (bg fill, hairline border) — or 'inverted': translucent dark
   *  fill with a cream border, for a fixed dark/violet backdrop that doesn't
   *  track the app's light/dark theme (e.g. auth screens). */
  tone?: 'default' | 'inverted';
}

/**
 * Checkbox — an 18px custom-styled box (gold fill + check glyph when
 * checked) driven by a visually-hidden native input, so it keeps real
 * keyboard/focus/form semantics instead of relying on browser checkbox
 * chrome, which varies too much across platforms to theme reliably.
 */
export function Checkbox({ checked, onChange, label, tone = 'default', id: idProp, style, ...props }: CheckboxProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const [focus, setFocus] = useState(false);
  const inverted = tone === 'inverted';

  return (
    <label htmlFor={id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', ...style }}>
      <span style={{ position: 'relative', flexShrink: 0, width: 18, height: 18, marginTop: 1 }}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', margin: 0, opacity: 0, cursor: 'pointer' }}
          {...props}
        />
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0, borderRadius: 5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1.5px solid ${checked ? (inverted ? 'rgb(var(--cream-bright))' : 'rgb(var(--gold))') : inverted ? 'rgba(255, 252, 219, .4)' : 'var(--line)'}`,
            background: checked ? (inverted ? 'rgb(var(--ink))' : 'rgb(var(--gold))') : inverted ? 'rgba(0, 0, 0, .15)' : 'var(--bg)',
            boxShadow: focus ? `0 0 0 3px ${inverted ? 'rgb(var(--cream-bright) / .25)' : 'rgb(var(--gold) / .25)'}` : 'none',
            transition: 'background .15s, border-color .15s, box-shadow .15s',
          }}
        >
          {checked && <i className="ti ti-check" style={{ fontSize: 12, color: inverted ? 'rgb(var(--cream-bright))' : 'rgb(var(--ink))' }} />}
        </span>
      </span>
      {label ? (
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 14, lineHeight: 1.4, color: inverted ? 'rgba(255, 252, 219, .65)' : 'var(--text-soft)' }}>
          {label}
        </span>
      ) : null}
    </label>
  );
}

export default Checkbox;
